import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Eye, 
  Calendar,
  Store,
  Package,
  TrendingUp,
  Share2,
  Download,
  Edit
} from 'lucide-react';
import ApiService from '../../services/api';

interface FinalStepProps {
  formData: any;
  onPublish: () => void;
  onPrevious: () => void;
}

const FinalStep: React.FC<FinalStepProps> = ({ formData, onPublish, onPrevious }) => {
  const [publishMode, setPublishMode] = useState<'draft' | 'schedule' | 'immediate'>('immediate');
  const [scheduleDate, setScheduleDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offerSummary, setOfferSummary] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  // جلب ملخص العرض من الـ API
  useEffect(() => {
    const fetchOfferSummary = async () => {
      setLoadingSummary(true);
      setError(null);
      try {
        // جلب البيانات الحقيقية من العرض الذي تم معالجته مع معالجة الأخطاء
        let summary = {};
        let productsResponse = { products: [] };
        let offerDetails = {};

        try {
          [summary, productsResponse, offerDetails] = await Promise.all([
            ApiService.getOfferSummary(formData.offer_id).catch(err => {
              console.warn('Failed to fetch summary:', err);
              return {};
            }),
            ApiService.getOfferProducts(formData.offer_id).catch(err => {
              console.warn('Failed to fetch products:', err);
              return { products: [] };
            }),
            ApiService.getOfferDetails(formData.offer_id).catch(err => {
              console.warn('Failed to fetch offer details:', err);
              return {};
            })
          ]);
        } catch (err) {
          console.error('Error in Promise.all:', err);
        }

        // استخراج array المنتجات من النتيجة مع التحقق
        const products = Array.isArray(productsResponse) ? productsResponse : (productsResponse?.products || []);

        // حساب الإحصائيات الحقيقية من البيانات المعالجة
        const approvedProducts = products.filter(p => p.status === 'approved').length;
        const rejectedProducts = products.filter(p => p.status === 'rejected').length;
        const totalProducts = products.length;
        
        // حساب متوسط الخصم الحقيقي
        const avgDiscount = products.length > 0 
          ? Math.round(products.reduce((sum, p) => sum + (p.extracted_data?.discount || 0), 0) / products.length)
          : 0;
        
        // حساب إجمالي التوفير الحقيقي
        const totalSavings = products.reduce((sum, p) => {
          if (p.extracted_data?.price_old && p.extracted_data?.price_new) {
            return sum + (p.extracted_data.price_old - p.extracted_data.price_new);
          }
          return sum;
        }, 0);

        // استخراج الفئات الحقيقية من المنتجات
        const categories = [...new Set(products.map(p => p.extracted_data?.category).filter(Boolean))];

        // إعداد البيانات مع قيم افتراضية
        setOfferSummary({
          title: offerDetails?.title || summary?.title || `عرض رقم ${formData.offer_id}`,
          merchant: offerDetails?.merchant_name || summary?.merchant || 'غير محدد',
          city: offerDetails?.city_name || summary?.city || 'غير محدد',
          startDate: offerDetails?.start_date || summary?.startDate || new Date().toISOString().split('T')[0],
          endDate: offerDetails?.end_date || summary?.endDate || new Date().toISOString().split('T')[0],
          totalProducts,
          approvedProducts,
          rejectedProducts,
          avgDiscount,
          totalSavings: Math.round(totalSavings),
          categories: categories.slice(0, 5), // أول 5 فئات
          processingStatus: summary?.processing_status || 'مكتملة',
          confidence: summary?.average_confidence || 0.85,
          detectedItems: summary?.detected_items_count || totalProducts
        });
      } catch (err) {
        console.error('Error fetching offer summary:', err);
        setError('حدث خطأ أثناء جلب بيانات العرض');
        // فقط في حالة فشل الـ API نهائياً، نعرض رسالة خطأ
        setOfferSummary(null);
      } finally {
        setLoadingSummary(false);
      }
    };
    
    if (formData.offer_id) {
      fetchOfferSummary();
    } else {
      setOfferSummary(null);
      setLoadingSummary(false);
    }
  }, [formData.offer_id]);

  const handlePublish = async () => {
    setLoading(true);
    setError(null);
    try {
      await ApiService.publishOffer(formData.offer_id);
      onPublish();
    } catch (e) {
      setError('حدث خطأ أثناء نشر العرض');
    } finally {
      setLoading(false);
    }
  };

  if (loadingSummary) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-8"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-lg">جاري تحميل ملخص العرض...</div>
        </div>
      </motion.div>
    );
  }

  if (error || !offerSummary) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-8"
      >
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-red-600 text-lg mb-4">{error || 'لم يتم العثور على بيانات العرض'}</div>
          <button onClick={onPrevious} className="btn-outline">
            العودة للخطوة السابقة
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-success-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            جاهز للنشر!
          </h2>
          <p className="text-gray-600">
            راجع ملخص العرض واختر طريقة النشر
          </p>
        </div>

        {/* ملخص العرض */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ملخص العرض</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* معلومات أساسية */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3 space-x-reverse">
                <Store className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="font-medium text-gray-900">{offerSummary.title}</div>
                  <div className="text-sm text-gray-600">{offerSummary.merchant} - {offerSummary.city}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 space-x-reverse">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="font-medium text-gray-900">فترة العرض</div>
                  <div className="text-sm text-gray-600">
                    من {offerSummary.startDate} إلى {offerSummary.endDate}
                  </div>
                </div>
              </div>
            </div>

            {/* إحصائيات */}
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{offerSummary.totalProducts}</div>
                <div className="text-xs text-gray-600">إجمالي المنتجات</div>
              </div>
              <div className="text-center p-3 bg-success-50 rounded-lg">
                <div className="text-2xl font-bold text-success-600">{offerSummary.approvedProducts}</div>
                <div className="text-xs text-gray-600">منتج مقبول</div>
              </div>
              <div className="text-center p-3 bg-primary-50 rounded-lg">
                <div className="text-2xl font-bold text-primary-600">{offerSummary.avgDiscount}%</div>
                <div className="text-xs text-gray-600">متوسط الخصم</div>
              </div>
              <div className="text-center p-3 bg-accent-50 rounded-lg">
                <div className="text-2xl font-bold text-accent-600">{offerSummary.totalSavings}</div>
                <div className="text-xs text-gray-600">إجمالي التوفير (ر.س)</div>
              </div>
            </div>

            {/* معلومات المعالجة */}
            {offerSummary.processingStatus && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm font-medium text-blue-900 mb-2">نتائج المعالجة بالذكاء الاصطناعي:</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">حالة المعالجة: </span>
                    <span className="font-medium">{offerSummary.processingStatus}</span>
                  </div>
                  {offerSummary.confidence && (
                    <div>
                      <span className="text-blue-700">متوسط الثقة: </span>
                      <span className="font-medium">{Math.round(offerSummary.confidence * 100)}%</span>
                    </div>
                  )}
                  {offerSummary.detectedItems && (
                    <div>
                      <span className="text-blue-700">عناصر مكتشفة: </span>
                      <span className="font-medium">{offerSummary.detectedItems}</span>
                    </div>
                  )}
                  {offerSummary.rejectedProducts > 0 && (
                    <div>
                      <span className="text-blue-700">منتجات مرفوضة: </span>
                      <span className="font-medium text-red-600">{offerSummary.rejectedProducts}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* الفئات */}
          <div className="mt-4">
            <div className="text-sm font-medium text-gray-700 mb-2">الفئات المشمولة:</div>
            <div className="flex flex-wrap gap-2">
              {offerSummary.categories.map((category, index) => (
                <span key={index} className="badge bg-gray-100 text-gray-700">
                  {category}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* خيارات النشر */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">خيارات النشر</h3>
          
          <div className="space-y-4">
            <label className="flex items-start space-x-3 space-x-reverse cursor-pointer">
              <input
                type="radio"
                name="publishMode"
                value="immediate"
                checked={publishMode === 'immediate'}
                onChange={(e) => setPublishMode(e.target.value as any)}
                className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <div>
                <div className="font-medium text-gray-900">نشر فوري</div>
                <div className="text-sm text-gray-600">نشر العرض مباشرة وإتاحته للمستخدمين</div>
              </div>
            </label>

            <label className="flex items-start space-x-3 space-x-reverse cursor-pointer">
              <input
                type="radio"
                name="publishMode"
                value="schedule"
                checked={publishMode === 'schedule'}
                onChange={(e) => setPublishMode(e.target.value as any)}
                className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">جدولة النشر</div>
                <div className="text-sm text-gray-600 mb-2">تحديد وقت محدد لنشر العرض</div>
                {publishMode === 'schedule' && (
                  <input
                    type="datetime-local"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="input w-full max-w-xs"
                  />
                )}
              </div>
            </label>

            <label className="flex items-start space-x-3 space-x-reverse cursor-pointer">
              <input
                type="radio"
                name="publishMode"
                value="draft"
                checked={publishMode === 'draft'}
                onChange={(e) => setPublishMode(e.target.value as any)}
                className="mt-1 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <div>
                <div className="font-medium text-gray-900">حفظ كمسودة</div>
                <div className="text-sm text-gray-600">حفظ العرض بدون نشر للمراجعة لاحقاً</div>
              </div>
            </label>
          </div>
        </div>

        {/* معاينة سريعة */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">معاينة العرض</h3>
            <button className="btn-outline btn-sm">
              <Eye className="w-4 h-4 ml-2" />
              معاينة كاملة
            </button>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-center">
              <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center mb-4">
                <Package className="w-12 h-12 text-gray-400" />
              </div>
              <h4 className="font-bold text-lg text-gray-900 mb-2">{offerSummary.title}</h4>
              <p className="text-gray-600 mb-4">{offerSummary.merchant}</p>
              <div className="flex items-center justify-center space-x-4 space-x-reverse text-sm text-gray-600">
                <span>{offerSummary.totalProducts} منتج</span>
                <span>•</span>
                <span>خصم حتى {offerSummary.avgDiscount}%</span>
                <span>•</span>
                <span>ينتهي {offerSummary.endDate}</span>
              </div>
            </div>
          </div>
        </div>

        {/* إجراءات إضافية */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-900 mb-3">إجراءات بعد النشر</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button className="btn-outline btn-sm">
              <Share2 className="w-4 h-4 ml-2" />
              مشاركة العرض
            </button>
            <button className="btn-outline btn-sm">
              <Download className="w-4 h-4 ml-2" />
              تصدير البيانات
            </button>
            <button className="btn-outline btn-sm">
              <TrendingUp className="w-4 h-4 ml-2" />
              متابعة الأداء
            </button>
          </div>
        </div>

        {/* أزرار التنقل */}
        <div className="flex justify-between items-center pt-6">
          <button
            onClick={onPrevious}
            className="btn-outline"
          >
            <Edit className="w-4 h-4 ml-2" />
            تعديل المراجعة
          </button>
          
          <div className="text-sm text-gray-500">
            {offerSummary.approvedProducts} منتج جاهز للنشر
          </div>
          
          <button
            onClick={handlePublish}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'جاري النشر...' : 'نشر العرض'}
          </button>
          {error && <div className="text-red-600 mt-2">{error}</div>}
        </div>
      </div>
    </motion.div>
  );
};

export default FinalStep;